import { useCallback, useContext, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import BrixigoPageLoader from "../../../components/common/BrixigoPageLoader";
import { AuthContext } from "../../Context/AuthContext";
import { useToast } from "../../Context/ToastContext";
import EmployeeProfileHeader from "./components/EmployeeProfileHeader";
import EmployeeProfileTabNav from "./components/EmployeeProfileTabNav";
import EmployeeProfileTabContent from "./tabs/EmployeeProfileTabContent";
import {
  useEmployeeCore,
  useEmployeeProfileSections,
  useOrgContext,
  useResolveEmployeeId,
} from "./useEmployeeProfile";
import {
  canExportProfile,
  visibleProfileTabGroups,
  visibleProfileTabs,
} from "./employeeProfilePermissions";
import { employeeDisplayName } from "./employeeProfileUtils";
import "../../../styles/employeeProfile.css";

export default function EmployeeProfilePage() {
  const { employeeId: routeParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  const { addToast } = useToast();

  const { resolving, employeeId, resolveError } = useResolveEmployeeId(routeParam);
  const { employee, loading, error, reload } = useEmployeeCore(employeeId);
  const orgId = useOrgContext();

  const tabs = useMemo(() => visibleProfileTabs(role), [role]);
  const tabGroups = useMemo(() => visibleProfileTabGroups(role), [role]);
  const tabParam = searchParams.get("tab") || "overview";
  const activeTab = tabs.some((t) => t.id === tabParam) ? tabParam : tabs[0]?.id || "overview";

  useEffect(() => {
    if (tabParam !== activeTab && tabs.length) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, tabParam, setSearchParams, tabs.length]);

  const sections = useEmployeeProfileSections(employeeId, activeTab, orgId);

  const goTab = useCallback(
    (tabId) => {
      setSearchParams({ tab: tabId });
    },
    [setSearchParams]
  );

  /** step: optional EmployeeCreate wizard step (1 Basic, 2 Employment, ...). */
  const onEdit = useCallback(
    (step) => {
      if (!employeeId) return;
      const query = Number.isInteger(step) ? `?step=${step}` : "";
      // Return here (same tab) after a successful save.
      navigate(`/employees/${employeeId}/edit${query}`, {
        state: { returnTo: `/employees/${employeeId}?tab=${encodeURIComponent(activeTab)}` },
      });
    },
    [employeeId, navigate, activeTab]
  );

  const onExport = useCallback(() => {
    if (!canExportProfile(role)) return;
    addToast(
      "Export will include only fields you are authorized to view. Backend export for a single employee profile is coming soon.",
      "info"
    );
  }, [addToast, role]);

  const onRetrySection = useCallback(
    (key) => {
      sections.invalidateSection(key);
    },
    [sections]
  );

  if (resolving || (loading && !employee)) {
    return (
      <div className="people-hub ep-page">
        <BrixigoPageLoader label="Loading employee profile…" />
      </div>
    );
  }

  if (resolveError === "not_found" || error === "not_found") {
    return (
      <div className="people-hub ep-page">
        <div className="ep-not-found">
          <h1>Employee Not Found</h1>
          <p>The employee ID or code does not match any record in your organisation.</p>
          <Link to="/employees" className="ep-btn ep-btn-primary">
            Back to Employees
          </Link>
        </div>
      </div>
    );
  }

  if (error === "forbidden") {
    return (
      <div className="people-hub ep-page">
        <div className="ep-not-found">
          <h1>Access Restricted</h1>
          <p>You do not have permission to view this employee profile.</p>
          <Link to="/employees" className="ep-btn ep-btn-primary">
            Back to Employees
          </Link>
        </div>
      </div>
    );
  }

  if (error === "load_failed" || !employee) {
    return (
      <div className="people-hub ep-page">
        <div className="ep-not-found">
          <h1>Unable to Load Profile</h1>
          <p>Something went wrong while loading this employee.</p>
          <button type="button" className="ep-btn ep-btn-primary" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="people-hub ep-page">
      <div className="ep-shell">
        <EmployeeProfileHeader employee={employee} role={role} onEdit={onEdit} onExport={onExport} />

        <div className="ep-tabs-sticky">
          <EmployeeProfileTabNav groups={tabGroups} activeTab={activeTab} onChange={goTab} />
        </div>

        <main className="ep-main" aria-label={`${employeeDisplayName(employee)} profile`}>
        <EmployeeProfileTabContent
          activeTab={activeTab}
          employee={employee}
          sections={sections}
          role={role}
          orgId={orgId}
          onEdit={onEdit}
          onGoTab={goTab}
          onRetrySection={onRetrySection}
        />
        </main>
      </div>
    </div>
  );
}
